import { z } from "zod";

export const ResourceTypeSchema = z.enum(["table", "staff", "crew", "room"]);

export const ResourceSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  type: ResourceTypeSchema,
  name: z.string(),
  capacityMin: z.number().int().min(1).default(1),
  capacityMax: z.number().int().min(1),
  // serviceIds: which catalog items this resource can fulfill (for staff/crew)
  serviceIds: z.array(z.string()).default([]),
  active: z.boolean().default(true),
});

// Weekly recurring availability rule
export const AvailabilityRuleSchema = z.object({
  id: z.string(),
  resourceId: z.string(),
  // days: 0=Sun ... 6=Sat
  days: z.array(z.number().int().min(0).max(6)),
  startTime: z.string().regex(/^\d{2}:\d{2}$/), // "09:00"
  endTime: z.string().regex(/^\d{2}:\d{2}$/),   // "17:00"
});

// One-off overrides: close for a holiday, add a special event slot, etc.
export const AvailabilityExceptionSchema = z.object({
  id: z.string(),
  resourceId: z.string(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // "2026-12-25"
  closed: z.boolean().default(false),
  // Override times when not closed
  startTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  endTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
});

export const BookingStatusSchema = z.enum([
  "pending",    // awaiting confirmation (request mode)
  "confirmed",
  "cancelled",
  "completed",
  "no_show",
]);

export const DepositStatusSchema = z.enum(["none", "required", "paid", "refunded"]);

export const BookingSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  resourceId: z.string(),
  customerId: z.string().optional(),
  customerName: z.string(),
  customerEmail: z.string().email(),
  customerPhone: z.string().optional(),
  partySize: z.number().int().min(1).optional(),
  serviceId: z.string().optional(), // for staff/crew bookings
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
  durationMinutes: z.number().int().positive(),
  notes: z.string().optional(),
  status: BookingStatusSchema.default("pending"),
  depositStatus: DepositStatusSchema.default("none"),
  depositAmountCents: z.number().int().min(0).default(0),
  stripePaymentIntentId: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Computed, never persisted — output of generateSlots()
export const TimeSlotSchema = z.object({
  resourceId: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  available: z.boolean(),
  remainingCapacity: z.number().int().min(0),
});

export type ResourceType = z.infer<typeof ResourceTypeSchema>;
export type Resource = z.infer<typeof ResourceSchema>;
export type AvailabilityRule = z.infer<typeof AvailabilityRuleSchema>;
export type AvailabilityException = z.infer<typeof AvailabilityExceptionSchema>;
export type BookingStatus = z.infer<typeof BookingStatusSchema>;
export type Booking = z.infer<typeof BookingSchema>;
export type TimeSlot = z.infer<typeof TimeSlotSchema>;
